import moment from 'moment';
import React from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';
import {reduxForm, formValueSelector} from 'redux-form';
import classNames from 'classnames';

import ErrorBlock from 'components/ErrorBlock';
import {
  bookingTypeInitialValue,
  SelectBookingTypeField,
  SelectBookingOrganisationField
} from 'components/Fields';

import {clearField} from 'utils/forms';
import {validEmail} from 'utils/validators';
import {formatDateForApi, DATE_FORMAT} from 'utils/datetime';

import {
  BOOKING_TYPE,
  searchTenants,
  createBooking,
  fetchBookingTypes,
  fetchBookingOrganisations,
  selectBookingTypes,
  selectBookingOrganisations
} from 'ducks/bookings';
import {fetchAvailableRooms, availableRoomsAreLoading} from 'ducks/rooms';
import {selectModal, MODALS as BASE_MODALS} from 'ducks/modals';
import {selectLocation, fetchLocation} from 'ducks/locations';
import {
  fetchBookingSetsByTenant,
  selectBookingSetsByTenant
} from 'ducks/bookingSets';
import {checkForNotMatchingRoomPurposesToBookingType} from 'ducks/allocations/utils';

import {searchTenants as sdkSearchTenants} from 'sdk/bookings';

import {
  LocationSection,
  locationInitialValues,
  BookingPeriodSection,
  RoomTypeSection,
  roomTypeInitialValues,
  RoomSection,
  roomInitialValues,
  TenantsSection,
  tenantsInitialValues,
  RentAndDepositSection,
  rentAndDepositInitialValues,
  CleanupPeriodSection,
  cleanupPeriodInitialValues,
  BookingSetSection,
  bookingSetInitialValues,
  buildBookingSetOption
} from './components';

import './styles.css';

const validate = values => {
  const errors = {primaryTenant: {}, secondaryTenant: {}};

  const primaryTenant = _.get(values, 'primaryTenant');
  const secondaryTenant = _.get(values, 'secondaryTenant');

  if (primaryTenant && _.get(primaryTenant, 'email')) {
    errors.primaryTenant['email'] = validEmail(primaryTenant.email.value);
  }

  if (secondaryTenant && _.get(secondaryTenant, 'email')) {
    errors.secondaryTenant['email'] = validEmail(secondaryTenant.email.value);

    if (!secondaryTenant.fullName) {
      errors.secondaryTenant['fullName'] = 'This field is required.';
    }
  }

  return errors;
};

const formSubmit = (values, _dispatch, props) => {
  const primaryTenant = {
    email: _.get(values, 'primaryTenant.email.value'),
    fullName: _.get(values, 'primaryTenant.fullName')
  };

  let secondaryTenant: any = null;

  if (_.get(values, 'secondaryTenant.email')) {
    secondaryTenant = {
      email: values.secondaryTenant.email.value,
      fullName: values.secondaryTenant.fullName
    };
  }

  const data = {
    locationRef: props.locationRef,
    roomType: values.roomType.object.id,
    room: _.get(values, 'room.value'),
    primaryTenant,
    secondaryTenant,
    pwRent: parseFloat(parseFloat(values.rentPerWeek).toFixed(2)),
    roomDeposit: parseFloat(parseFloat(values.roomDeposit).toFixed(2)),
    startDate: formatDateForApi(values.startDate),
    endDate: formatDateForApi(values.endDate),
    createCleanupPeriod: values.createCleanupPeriod,
    cleanupPeriodDays: values.cleanupPeriodDays,
    bookingType: values.bookingType.value,
    bookingOrganisation: _.get(values, 'bookingOrganisation.value'),
    bookingSet: _.get(values, 'bookingSet.value')
  };

  props.createBooking(data, props.getExtraActions());
};

class BookingCreateForm extends React.Component<any> {
  componentDidMount() {
    this.props.fetchBookingTypes();
    this.props.fetchBookingOrganisations();
    this.props.fetchLocation({locationRef: this.props.locationRef});

    if (this.props.initialBookingSet) {
      this.props.fetchBookingSetsByTenant({
        locationRef: this.props.locationRef,
        tenantId: this.props.initialBookingSet.tenant.id
      });
    }
  }

  fetchTenants = value => sdkSearchTenants({value});

  clearTenant = tenant => {
    this.props.change('bookingSet', null);

    clearField(this, {field: `${tenant}.fullName`});
    clearField(this, {field: `${tenant}.email`});
  };

  onPrimaryTenantChange = (primaryTenant: {object}) => {
    // This code expects the default prefilled value for "bookingSet" to be "Create new"
    if (_.get(primaryTenant.object, 'id')) {
      this.props.fetchBookingSetsByTenant({
        locationRef: this.props.locationRef,
        tenantId: primaryTenant.object.id
      });
    }
  };

  fetchAvailableRooms = (startDate, endDate, roomType, cleanupPeriodDays) => {
    // The method receives startDate endDate and roomType
    // rather than taking them from this.props because it is invoked on change
    if (_.isNil(startDate) || _.isNil(endDate) || _.isNil(roomType)) return;

    const locationRef = this.props.locationRef;
    const extraActions = this.props.storeDataExtraActions(data => ({
      availableRooms: data
    }));

    this.props.fetchAvailableRooms(
      {
        locationRef,
        roomType: roomType.value,
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate),
        cleanupPeriodDays: cleanupPeriodDays
      },
      extraActions
    );
  };

  onStartDateChange = startDate => {
    const {
      endDate,
      roomType,
      createCleanupPeriodValue,
      cleanupPeriodDaysValue
    } = this.props;

    this.fetchAvailableRooms(
      startDate,
      endDate,
      roomType,
      createCleanupPeriodValue ? cleanupPeriodDaysValue : null
    );
  };

  onEndDateChange = endDate => {
    const {
      startDate,
      roomType,
      createCleanupPeriodValue,
      cleanupPeriodDaysValue
    } = this.props;

    this.fetchAvailableRooms(
      startDate,
      endDate,
      roomType,
      createCleanupPeriodValue ? cleanupPeriodDaysValue : null
    );
  };

  onRoomTypeChange = roomType => {
    const {
      startDate,
      endDate,
      createCleanupPeriodValue,
      cleanupPeriodDaysValue
    } = this.props;

    // resetSection resets the field to be pristine again;
    // In this way when fethching data again, the first room from the newly fetched rooms will be set as initial value
    this.props.resetSection('room');

    this.fetchAvailableRooms(
      startDate,
      endDate,
      roomType,
      createCleanupPeriodValue ? cleanupPeriodDaysValue : null
    );
  };

  onCleanupPeriodCheckboxChange = event => {
    const checked = event.target.checked;

    const {startDate, endDate, roomType, cleanupPeriodDaysValue} = this.props;

    this.fetchAvailableRooms(
      startDate,
      endDate,
      roomType,
      checked ? cleanupPeriodDaysValue : null
    );
  };

  onCleanupPeriodDaysChange = event => {
    const days = event.target.value;
    if (days === '' || days === '0') return;

    const {startDate, endDate, roomType, createCleanupPeriodValue} = this.props;

    this.fetchAvailableRooms(
      startDate,
      endDate,
      roomType,
      createCleanupPeriodValue ? days : null
    );
  };

  onBookingTypeChange = () => clearField(this, {field: 'bookingOrganisation'});

  render() {
    const {
      error,
      location,
      roomTypes,
      primaryTenant,
      secondaryTenant,
      roomType,
      availableRooms,
      noAvailableRooms,
      createCleanupPeriodValue,
      bookingTypes,
      bookingOrganisations,
      disableBookingOrganisation,
      showForm,
      disablePrimaryTenantEmail,
      bookingSetsByTenant,
      selectedBookingType,
      roomPurposesForPeriod,
      availableRoomsAreLoading,
      startDate,
      endDate
    } = this.props;

    const hasNotMatchingRoomPurposeToBookingType = checkForNotMatchingRoomPurposesToBookingType(
      {
        roomPurposesForPeriod,
        bookingType: _.get(selectedBookingType, 'label')
      }
    );

    const startDateGreaterThаnEndDate =
      startDate &&
      endDate &&
      moment(startDate, DATE_FORMAT) > moment(endDate, DATE_FORMAT);

    const isOrganisationRequired =
      selectedBookingType &&
      selectedBookingType.object.name === BOOKING_TYPE.CORPORATE;
    return (
      <form className={classNames('BookingCreateForm', {hidden: !showForm})}>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <div>
          <SelectBookingTypeField
            onBookingTypeChange={this.onBookingTypeChange}
            bookingTypes={bookingTypes}
          />
          <SelectBookingOrganisationField
            isOrganisationRequired={isOrganisationRequired}
            disableBookingOrganisation={disableBookingOrganisation}
            bookingOrganisations={bookingOrganisations}
          />
        </div>
        <LocationSection location={location} />

        <BookingPeriodSection
          onStartChange={this.onStartDateChange}
          onEndChange={this.onEndDateChange}
          startDateGreaterThаnEndDate={startDateGreaterThаnEndDate}
        />

        <CleanupPeriodSection
          createCleanupPeriodValue={createCleanupPeriodValue}
          onCleanupPeriodCheckboxChange={this.onCleanupPeriodCheckboxChange}
          onCleanupPeriodDaysChange={this.onCleanupPeriodDaysChange}
        />

        <RoomTypeSection
          roomTypes={roomTypes}
          onRoomTypeChange={this.onRoomTypeChange}
        />

        <RoomSection
          availableRooms={availableRooms}
          noAvailableRooms={noAvailableRooms}
          hasNotMatchingRoomPurposeToBookingType={
            hasNotMatchingRoomPurposeToBookingType
          }
          availableRoomsAreLoading={availableRoomsAreLoading}
        />

        <TenantsSection
          primaryTenant={primaryTenant}
          secondaryTenant={secondaryTenant}
          fetchTenants={this.fetchTenants}
          clearTenant={this.clearTenant}
          roomType={roomType}
          onPrimaryTenantChange={this.onPrimaryTenantChange}
          disablePrimaryTenantEmail={disablePrimaryTenantEmail}
        />

        <BookingSetSection bookingSets={bookingSetsByTenant} />

        <RentAndDepositSection />
      </form>
    );
  }
}

const formSelector = formValueSelector('createBooking');

const mapStateToProps = state => {
  const modal = selectModal(state, BASE_MODALS.CREATE_BOOKING);

  const availableRooms = _.get(modal, 'data.availableRooms', []);
  const noAvailableRooms =
    !_.isNil(_.get(modal, 'data.availableRooms')) && _.isEmpty(availableRooms);

  const initialBookingSet = _.get(modal, 'data.initialBookingSet', null);
  let initialValues: any = {
    ...locationInitialValues(state),
    ...roomTypeInitialValues(state),
    ...roomInitialValues(state),
    ...tenantsInitialValues(state),
    ...rentAndDepositInitialValues(state),
    ...cleanupPeriodInitialValues(),
    ...bookingTypeInitialValue(state),
    ...bookingSetInitialValues(state)
  };

  if (initialBookingSet) {
    initialValues = {
      ...initialValues,
      primaryTenant: {
        fullName: initialBookingSet.tenant.fullName,
        email: {
          value: initialBookingSet.tenant.email,
          label: initialBookingSet.tenant.email,
          object: initialBookingSet.tenant
        }
      },
      bookingSet: buildBookingSetOption(initialBookingSet)
    };
  }

  const bookingTypes = selectBookingTypes(state);
  const bookingOrganisations = selectBookingOrganisations(state);

  const startDate = formSelector(state, 'startDate');
  const endDate = formSelector(state, 'endDate');
  const primaryTenant = formSelector(state, 'primaryTenant.email');
  const secondaryTenant = formSelector(state, 'secondaryTenant.email');
  const roomTypeFormValue = formSelector(state, 'roomType');
  const createCleanupPeriodValue = formSelector(state, 'createCleanupPeriod');
  const cleanupPeriodDaysValue = formSelector(state, 'cleanupPeriodDays');

  const selectedBookingType = formSelector(state, 'bookingType');
  const disableBookingOrganisation =
    selectedBookingType && !selectedBookingType.object.enableOrganisation;

  return {
    initialValues,
    roomType: roomTypeFormValue,
    startDate,
    endDate,
    primaryTenant,
    secondaryTenant,
    availableRooms,
    noAvailableRooms,
    createCleanupPeriodValue,
    cleanupPeriodDaysValue,
    bookingTypes,
    bookingOrganisations,
    disableBookingOrganisation,
    initialBookingSet,
    bookingSetsByTenant: selectBookingSetsByTenant(state),
    location: selectLocation(state),
    disablePrimaryTenantEmail: !_.isNull(initialBookingSet),
    selectedBookingType,
    roomPurposesForPeriod: _.get(
      formSelector(state, 'room'),
      'object.roomPurposesForPeriod',
      []
    ),
    availableRoomsAreLoading: availableRoomsAreLoading(state)
  };
};

const mapDispatchToProps = {
  searchTenants,
  createBooking,
  fetchAvailableRooms,
  fetchBookingTypes,
  fetchBookingOrganisations,
  fetchLocation,
  fetchBookingSetsByTenant
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'createBooking',
    onSubmit: formSubmit,
    validate,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true
  })(BookingCreateForm)
);
